import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { eventMatches } from "@omni/sdk/protocol";

import { Icon } from "../components/Icon";
import { api, ApiError, resolveUrl } from "../lib/api";
import { appSession, cachedAppSession } from "../lib/appSessions";
import { attachBridge, type AppSession } from "../lib/bridge";
import { useAppCamera, type CameraView } from "../lib/camera";
import { onEvent } from "../lib/events";
import { installed, localDocument, opensLocally, syncBundles } from "../lib/installer";
import { openExternal, platform, shareLink } from "../lib/platform";
import { load } from "../lib/storage";
import { useTheme } from "../lib/theme";
import { useToast } from "../lib/toast";

// ADR-0002: no allow-same-origin — apps can't reach the shell's storage or tokens.
const SANDBOX = "allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads allow-modals";

export function AppHost() {
  const { appId = "" } = useParams();
  const location = useLocation();
  const nav = useNavigate();
  const toast = useToast();
  const { theme } = useTheme();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const sessionRef = useRef<Promise<AppSession> | null>(null);
  const latestSession = useRef<AppSession | null>(null);
  const bridgeRef = useRef<ReturnType<typeof attachBridge> | null>(null);
  const themeRef = useRef(theme);
  const [session, setSession] = useState<AppSession | null>(null);
  const [frame, setFrame] = useState<{ src?: string; srcDoc?: string; key: number } | null>(null);
  const [title, setTitle] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [menu, setMenu] = useState(false);
  const [asking, setAsking] = useState(true);
  const [ask, setAsk] = useState<{ permission: string; resolve: (ok: boolean) => void } | null>(null);
  const askingNow = useRef<Promise<boolean> | null>(null);
  const askPermission = (permission: string) => {
    askingNow.current ??= new Promise<boolean>((resolve) =>
      setAsk({
        permission,
        resolve: (ok) => {
          askingNow.current = null;
          setAsk(null);
          resolve(ok);
        },
      }),
    );
    return askingNow.current;
  };
  const grant = async () => {
    if (!ask) return;
    try {
      await api(`/api/apps/${appId}/permissions`, { method: "POST", body: { permission: ask.permission } });
      await renew();
      ask.resolve(true);
    } catch (e) {
      toast((e as Error).message, "error");
      ask.resolve(false);
    }
  };
  const camera = useAppCamera();
  const cameraApi = useRef(camera.api);
  cameraApi.current = camera.api;


  const prefix = `/apps/${appId}`;
  const initialRoute = useRef(location.pathname.slice(prefix.length) || "/");

  // Developer override: point an app at its local Vite dev server (Settings → Developer).
  const devUrl = load<Record<string, string>>("omni.devOverrides", {})[appId];

  const track = (p: Promise<AppSession>) => {
    sessionRef.current = p;
    p.then((s) => {
      latestSession.current = s;
      setSession(s);
      setTitle(s.app.name);
    }).catch(() => undefined);
    return p;
  };
  const renew = () => track(appSession(appId, true));

  // ADR-0024: an installed app starts from the device (or its versioned, cached URL)
  // at once; the session is fetched in parallel, or reused while it's still valid.
  useEffect(() => {
    setError(null);
    setFrame(null);
    const cached = cachedAppSession(appId);
    setSession(cached);
    latestSession.current = cached;
    const inst = installed(appId);
    setTitle(inst?.name ?? cached?.app.name ?? "");
    const pending = track(appSession(appId));
    pending
      .then((s) => {
        // A newer version on the server: fetch it now, it opens next time
        if (s.bundle && inst && s.bundle.hash !== inst.hash) void syncBundles().catch(() => undefined);
        if (!inst && !devUrl) setFrame((f) => f ?? { src: resolveUrl(s.frontend_url), key: 1 });
      })
      .catch((e: ApiError) => {
        if (e.status === 402) nav(`/marketplace/${appId}`, { replace: true });
        else setError(e.message);
      });
    if (devUrl) setFrame({ src: devUrl, key: 1 });
    else if (inst && opensLocally())
      localDocument(appId)
        .then((doc) => setFrame(doc ? { srcDoc: doc, key: 1 } : { src: resolveUrl(inst.base_url + inst.entry), key: 1 }))
        .catch(() => setFrame({ src: resolveUrl(inst.base_url + inst.entry), key: 1 }));
    else if (inst) setFrame({ src: resolveUrl(inst.base_url + inst.entry), key: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId]);

  // Attach the bridge before the iframe loads so the app's first message is caught.
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!frame || !iframe) return;
    const bridge = attachBridge(iframe, {
      appId,
      platform,
      getSession: () => sessionRef.current!,
      renewSession: renew,
      askPermission: (p) => askPermission(p),
      camera: {
        open: (p) => cameraApi.current.open(p),
        move: (p) => cameraApi.current.move(p),
        capture: () => cameraApi.current.capture(),
        close: () => cameraApi.current.close(),
      },
      theme: () => themeRef.current,
      route: () => initialRoute.current,
      toast,
      setTitle,
      openExternal,
      share: shareLink,
      setRoute: (path) => {
        const clean = path.startsWith("/") ? path : `/${path}`;
        nav(`${prefix}${clean === "/" ? "" : clean}`, { replace: true });
      },
      navigate: (t) => {
        if (t.to === "home") nav("/");
        else if (t.to === "profile") nav("/profile");
        else if (t.to === "marketplace") nav(t.appId ? `/marketplace/${t.appId}` : "/marketplace");
        else if (t.to === "app") nav(`/apps/${t.appId}${t.path ?? ""}`);
      },
    });
    bridgeRef.current = bridge;
    // ADR-0025: live events this app is allowed to hear
    const offEvents = onEvent((e) => {
      const s = latestSession.current;
      if (s && eventMatches(s.events ?? [], e.type)) bridge.emit("bus", e);
    });
    return () => {
      offEvents();
      camera.close(); // a reloaded or closed app never keeps the camera on
      bridge.dispose();
      bridgeRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frame?.key, !!frame]);

  useEffect(() => {
    themeRef.current = theme;
    bridgeRef.current?.emit("theme", theme);
  }, [theme]);

  useEffect(() => {
    const fn = () => bridgeRef.current?.emit("visibility", { visible: document.visibilityState === "visible" });
    document.addEventListener("visibilitychange", fn);
    return () => document.removeEventListener("visibilitychange", fn);
  }, []);


  const acceptPermissions = async () => {
    try {
      await api(`/api/apps/${appId}/permissions`, { method: "POST" });
      await renew();
      setAsking(false);
      setFrame((f) => f && { ...f, key: f.key + 1 }); // start the app again with what it may now do
    } catch (e) {
      toast((e as Error).message, "error");
    }
  };
  // The camera is asked for when the app needs it (dialog above), not in the banner
  const pending = asking ? (session?.pending_permissions ?? []).filter((p) => p.permission !== "camera") : [];

  if (error)
    return (
      <main className="page no-tabs">
        <p className="error">{error}</p>
        <button className="btn" onClick={() => nav("/")}>Back to my space</button>
      </main>
    );

  return (
    <div className={`apphost${session?.app.fullscreen ? " fullscreen" : ""}`}>
      {!session?.app.fullscreen && (
        <header className="apphost-bar">
          <div className="apphost-row">
            <button className="back-link" onClick={() => nav("/")} aria-label="Back to your space">
              <Icon name="chevron-left" size={20} />Space
            </button>
            <span className="title">{title}</span>
            <span className="spacer">
              <button className="icon-btn" aria-label="App menu" aria-expanded={menu} onClick={() => setMenu(!menu)}>
                <Icon name="dots" size={20} />
              </button>
            </span>
          </div>
        </header>
      )}
      {menu && (
        <div className="menu" role="menu" onMouseLeave={() => setMenu(false)}>
          <button role="menuitem" onClick={() => nav(`/?add=${appId}`)}><Icon name="layout" size={20} />Add to Space</button>
          <button role="menuitem" onClick={() => nav(`/marketplace/${appId}`)}><Icon name="apps" size={20} />About this app</button>
          <button role="menuitem" onClick={() => { setMenu(false); setFrame((f) => f && { ...f, key: f.key + 1 }); }}>
            <Icon name="arrow-right" size={20} />Reload
          </button>
        </div>
      )}
      {pending.length > 0 && (
        <div className="apphost-ask" role="alert">
          <span className="grow small">
            <span className="b">{title || "This app"} also asks to:</span> {pending.map((p) => p.description).join("; ")}
          </span>
          <button className="btn btn-sm btn-ghost" onClick={() => setAsking(false)}>Not now</button>
          <button className="btn btn-sm btn-primary" onClick={acceptPermissions}>Allow</button>
        </div>
      )}
      {frame && (
        <iframe
          key={`${appId}:${frame.key}`}
          ref={iframeRef}
          className="apphost-frame"
          title={title || appId}
          src={frame.srcDoc ? undefined : frame.src}
          srcDoc={frame.srcDoc}
          sandbox={SANDBOX}
          allow="clipboard-write; web-share"
          referrerPolicy="no-referrer"
        />
      )}
      {!frame && <p className="muted center page">Opening…</p>}
      {ask && (
        <div className="perm-backdrop">
          <div className="perm-dialog card stack" role="alertdialog" aria-modal="true" aria-labelledby="perm-title">
            <span className="perm-ic"><Icon name={ask.permission === "camera" ? "camera" : "shield"} size={26} /></span>
            <h2 id="perm-title" className="center">{ask.permission === "camera" ? `Let ${title || "this app"} use the camera?` : `Allow ${title || "this app"}?`}</h2>
            <p className="small muted center">
              {ask.permission === "camera"
                ? "Omni shows the camera inside the app while you're on its screen. The app only gets the photos you take."
                : (session?.pending_permissions ?? []).find((p) => p.permission === ask.permission)?.description ?? ask.permission}
            </p>
            <div className="row">
              <button className="btn grow" onClick={() => ask.resolve(false)}>Don't allow</button>
              <button className="btn btn-primary grow" onClick={grant}>Allow</button>
            </div>
          </div>
        </div>
      )}
      {camera.view && iframeRef.current && (
        <CameraLayer frame={iframeRef.current} view={camera.view} video={camera.video} />
      )}
    </div>
  );
}

/** The live preview, drawn over the part of the app that asked for it and clipped to the app. */
function CameraLayer({ frame, view, video }: { frame: HTMLIFrameElement; view: CameraView; video: React.RefObject<HTMLVideoElement> }) {
  const box = frame.getBoundingClientRect();
  const { rect, guide, radius, flash } = view;
  return (
    <div className="app-camera-clip" style={{ left: box.left, top: box.top, width: box.width, height: box.height }} aria-hidden>
      <div className="app-camera" style={{ left: rect.x, top: rect.y, width: rect.width, height: rect.height, borderRadius: radius }}>
        <video ref={video} playsInline muted autoPlay />
        {guide && (
          <div className={`app-camera-guide ${guide}`}>
            <span /><span /><span /><span />
          </div>
        )}
        {flash && <div className="app-camera-flash" />}
      </div>
    </div>
  );
}
